// Booking strategy for direct Newbook Cloud tenants (the classic widget, a
// park's WordPress embed of it, and the /online/ UI).
//
// Every tenant's booking page rehydrates the search from the same query:
//
//   available_from=<dd MMM yyyy>
//   available_to=<dd MMM yyyy>
//   nights=<N>
//   adults=<N>
//
// appended to `site.reserveUrl`, which already carries whatever category
// filter that tenant's page honours (`force_category_id`, plus
// `category_type_id` on a parent-grouped tenant; nothing on the /online/
// UI, where `force_category_id` renders a hard Warning page). Newbook
// serializes spaces as `+` in the browser URL, e.g. `13+Aug+2026`.
//
// A tenant's strategy module is `newbookStrategy({ overviewUrl, refNotes })`:
// `overviewUrl` is the whole-park booking page the preview link opens (null
// to preview the site's own reserveUrl; a function of the site when the
// agency's facilities are separate Newbook instances), `refNotes` says what
// the tenant's reserveUrl encodes.

import { format } from "date-fns";
import {
  resolveCheckout,
  resolveMinCapacity,
  resolveMinStayLength,
} from "./_context.js";

function formatNewbookDate(date) {
  return format(date, "dd MMM yyyy");
}

export function newbookStrategy({ overviewUrl = null, refNotes }) {
  return {
    buildBookingUrl(ctx) {
      const { site, arrivalDate } = ctx;
      const checkout = resolveCheckout(ctx);
      const nights = resolveMinStayLength(ctx);
      const adults = resolveMinCapacity(ctx);

      const url = new URL(site.reserveUrl);
      url.searchParams.set("available_from", formatNewbookDate(arrivalDate));
      url.searchParams.set("available_to", formatNewbookDate(checkout));
      url.searchParams.set("nights", String(nights));
      url.searchParams.set("adults", String(adults));
      return url.toString();
    },

    buildPreviewUrl(site) {
      if (typeof overviewUrl === "function") return overviewUrl(site);
      return overviewUrl ?? site.reserveUrl;
    },

    describeBookingInputs() {
      return {
        inputs: {
          arrivalDate: {
            status: "required",
            notes: "Encoded as &available_from=<dd MMM yyyy>.",
          },
          departureDate: {
            status: "required",
            notes:
              "Encoded as &available_to=<dd MMM yyyy>; resolved from arrival + minStayLength when not supplied.",
          },
          ref: {
            status: "ignored",
            notes: refNotes,
          },
          partySize: {
            status: "defaulted",
            defaultValue: 1,
            notes:
              "Encoded as &adults=<N>; defaults to AvailabilityRequest.minCapacity, then 1.",
          },
        },
      };
    },
  };
}

const PARENT_AND_CHILD =
  "site.reserveUrl already encodes Newbook category_type_id and force_category_id for the child category.";
const CATEGORY_ONLY =
  "site.reserveUrl already encodes Newbook force_category_id for the category.";

// Keyed by Agency.scanner. The overview is the park's whole booking page.
export const newbookTenants = {
  YardieHomestead: newbookStrategy({
    overviewUrl:
      "https://bookingsau.newbook.cloud/yardie/index.php?place_booking",
    refNotes: PARENT_AND_CHILD,
  }),
  PeoplesParkCoralBay: newbookStrategy({
    overviewUrl: "https://peoplesparkcoralbay.com.au/book-now/",
    refNotes: CATEGORY_ONLY,
  }),
  NingalooCaravanHolidayResort: newbookStrategy({
    refNotes:
      "No per-category deep link exists — force_category_id is broken on the Newbook /online/ UI. The user picks the category card matching the Schnerp site name.",
  }),
  BullaraStation: newbookStrategy({
    overviewUrl:
      "https://bookingsau.newbook.cloud/bullarastation/index.php?place_booking",
    refNotes: CATEGORY_ONLY,
  }),
  BarnHillStation: newbookStrategy({
    overviewUrl:
      "https://bookingsau.newbook.cloud/barnhill/index.php?place_booking",
    refNotes: PARENT_AND_CHILD,
  }),
  KalbarriAnchorage: newbookStrategy({
    overviewUrl:
      "https://bookingsau.newbook.cloud/kalbarrianchorage/index.php?place_booking",
    refNotes: CATEGORY_ONLY,
  }),
  BathersParadise: newbookStrategy({
    overviewUrl:
      "https://bookingsau.newbook.cloud/bathersparadise/index.php?place_booking",
    refNotes: CATEGORY_ONLY,
  }),
  EsperancePinkLake: newbookStrategy({
    overviewUrl: "https://esperancepinklake.com.au/book-online/",
    refNotes: CATEGORY_ONLY,
  }),
  // Six parks, each its own `/online/` instance: the overview is the site's
  // own park page, its reserveUrl without the category filter.
  SunshineCoastHolidayParks: newbookStrategy({
    overviewUrl: (site) => {
      const url = new URL(site.reserveUrl);
      url.searchParams.delete("force_category_id");
      return url.toString();
    },
    refNotes: CATEGORY_ONLY,
  }),
};
